import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { ArrowRight, Check, Eye, EyeOff, Lock, Mail, Phone, User, UserPlus } from "lucide-react";

export default function Register() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", confirmPassword: "" });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (loading) return null;
  if (user && user !== false) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
  }

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) return toast.error("Sila masukkan nama penuh.");
    if (!form.email.trim()) return toast.error("Sila masukkan email.");
    if (!acceptedTerms) return toast.error("Sila setuju dengan Terma & Syarat dan Dasar Privasi.");
    if (form.password.length < 6) return toast.error("Kata laluan minimum 6 aksara.");
    if (form.password !== form.confirmPassword) return toast.error("Kata laluan tidak sepadan.");

    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/register", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        password: form.password,
      });
      localStorage.setItem("azmiey_token", data.token);
      toast.success("Akaun berjaya didaftar");
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Pendaftaran gagal");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0D] text-zinc-900">
      <header className="relative overflow-hidden border-b border-white/10 bg-[#29292D]">
        <div className="absolute -left-10 top-0 h-44 w-44 rounded-full bg-violet-500/15 blur-3xl" />
        <div className="absolute right-0 top-[-2rem] h-52 w-52 rounded-full bg-violet-600/20 blur-3xl" />
        <div className="relative mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-baseline gap-1">
            <span className="font-heading text-[1.65rem] font-black tracking-[-0.06em] text-white sm:text-[2rem]">AZMIEY</span>
            <span className="font-heading text-[1.65rem] font-black tracking-[-0.06em] text-violet-500 sm:text-[2rem]">STORE</span>
          </Link>
          <p className="mt-3 text-[0.68rem] font-medium uppercase tracking-[0.32em] text-violet-300 sm:text-[0.72rem]">
            Daftar akaun baharu
          </p>
        </div>
      </header>

      <main className="mx-auto flex max-w-5xl items-center justify-center px-4 py-8 sm:px-6 lg:py-12">
        <div className="w-full max-w-[540px]">
          <div className="rounded-[24px] border border-violet-100/80 bg-white p-5 shadow-[0_30px_90px_rgba(16,10,26,0.14)] sm:p-8">
            <div className="mb-6 flex justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-violet-100 text-violet-600 shadow-[0_18px_35px_rgba(124,58,237,0.18)]">
                <UserPlus className="h-8 w-8" />
              </div>
            </div>

            <div className="text-center">
              <h1 className="font-heading text-[2rem] font-black leading-[1.04] tracking-[-0.05em] text-zinc-900 sm:text-[2.5rem]">
                Daftar Akaun
                <span className="mt-1 block text-violet-600">Baharu</span>
              </h1>
              <p className="mt-3 text-sm leading-6 text-zinc-600 sm:text-[15px]">
                Lengkapkan maklumat anda untuk memulakan perjalanan bersama kami.
              </p>
            </div>

            <form onSubmit={onSubmit} data-testid="register-form" className="mt-7 space-y-5">
              <div>
                <label className="mb-2 block text-sm font-medium text-zinc-700">Nama Penuh</label>
                <div className="relative">
                  <User className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                  <Input
                    id="register-name-input"
                    data-testid="register-name-input"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Nama Penuh"
                    className="h-[52px] rounded-2xl border border-zinc-200 bg-zinc-50 pl-11 pr-4 text-[15px] text-zinc-900 placeholder:text-zinc-400 focus-visible:border-violet-300 focus-visible:ring-violet-300"
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-zinc-700">Email</label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                  <Input
                    id="register-email-input"
                    data-testid="register-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder="Email"
                    className="h-[52px] rounded-2xl border border-zinc-200 bg-zinc-50 pl-11 pr-4 text-[15px] text-zinc-900 placeholder:text-zinc-400 focus-visible:border-violet-300 focus-visible:ring-violet-300"
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-zinc-700">No. Telefon</label>
                <div className="relative">
                  <Phone className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                  <Input
                    id="register-phone-input"
                    data-testid="register-phone-input"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="No. Telefon"
                    className="h-[52px] rounded-2xl border border-zinc-200 bg-zinc-50 pl-11 pr-4 text-[15px] text-zinc-900 placeholder:text-zinc-400 focus-visible:border-violet-300 focus-visible:ring-violet-300"
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-zinc-700">Kata Laluan</label>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                  <Input
                    id="register-password-input"
                    data-testid="register-password-input"
                    type={showPassword ? "text" : "password"}
                    required
                    value={form.password}
                    onChange={set("password")}
                    placeholder="Kata Laluan"
                    className="h-[52px] rounded-2xl border border-zinc-200 bg-zinc-50 pl-11 pr-12 text-[15px] text-zinc-900 placeholder:text-zinc-400 focus-visible:border-violet-300 focus-visible:ring-violet-300"
                  />
                  <button
                    type="button"
                    aria-label={showPassword ? "Sembunyikan kata laluan" : "Tunjukkan kata laluan"}
                    onClick={() => setShowPassword((value) => !value)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-zinc-500 transition hover:text-violet-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-zinc-700">Sahkan Kata Laluan</label>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                  <Input
                    id="register-password-confirm-input"
                    data-testid="register-password-confirm-input"
                    type={showConfirmPassword ? "text" : "password"}
                    required
                    value={form.confirmPassword}
                    onChange={set("confirmPassword")}
                    placeholder="Sahkan Kata Laluan"
                    className="h-[52px] rounded-2xl border border-zinc-200 bg-zinc-50 pl-11 pr-12 text-[15px] text-zinc-900 placeholder:text-zinc-400 focus-visible:border-violet-300 focus-visible:ring-violet-300"
                  />
                  <button
                    type="button"
                    aria-label={showConfirmPassword ? "Sembunyikan kata laluan" : "Tunjukkan kata laluan"}
                    onClick={() => setShowConfirmPassword((value) => !value)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-zinc-500 transition hover:text-violet-600"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-zinc-200 bg-zinc-50 px-3 py-3 text-sm text-zinc-600">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-violet-600 focus:ring-violet-500"
                />
                <span>
                  Saya bersetuju dengan <a href="#" className="font-semibold text-violet-600 hover:text-violet-700">Terma &amp; Syarat</a> dan <a href="#" className="font-semibold text-violet-600 hover:text-violet-700">Dasar Privasi</a>
                </span>
              </label>

              <button
                type="submit"
                disabled={submitting}
                data-testid="register-submit-button"
                id="register-submit-button"
                className="inline-flex h-[54px] w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-violet-600 to-violet-500 text-base font-semibold text-white shadow-[0_18px_35px_rgba(124,58,237,0.35)] transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_20px_40px_rgba(124,58,237,0.4)] disabled:cursor-not-allowed disabled:opacity-70"
              >
                {submitting ? "Memuat..." : (
                  <>
                    <Check className="h-5 w-5" />
                    Daftar Sekarang
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </form>

            <p className="mt-7 text-center text-sm text-zinc-600">
              Sudah ada akaun?{" "}
              <Link to="/login" className="font-semibold text-violet-600 transition hover:text-violet-700">
                Log masuk
              </Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
